/** Coarse presentation ticks avoid a per-frame state update for countdowns. */
const TICK_MS = 100;

/**
 * `tick` updates every TICK_MS; `frame` updates on every animation frame, for traces stepped tile
 * by tile (post-match recap, tutorial) whose steps can each last less than one tick.
 */
type ClockCadence = 'tick' | 'frame';

/**
 * A wall clock for countdowns and post-match playback. Ticks only while a round needs it and the page is
 * visible; waking from hidden re-reads the time so derived countdowns jump correctly.
 */
export class Clock {
  now = $state(Date.now());
  visible = $state(true);
  private timer = 0;
  private frame = 0;
  private requested = false;
  private unmount: (() => void) | undefined;

  constructor(private readonly cadence: ClockCadence = 'tick') {}

  /** Attach page-visibility tracking; returns the teardown for `onMount`. */
  mount(): () => void {
    this.visible = document.visibilityState === 'visible';
    const onVisibility = () => {
      this.visible = document.visibilityState === 'visible';
      this.run(this.requested);
    };
    document.addEventListener('visibilitychange', onVisibility);
    this.unmount = () => document.removeEventListener('visibilitychange', onVisibility);
    return () => this.dispose();
  }

  /** Start or stop ticking; always refreshes `now` so consumers update when the clock wakes. */
  run(active: boolean): void {
    this.requested = active;
    this.now = Date.now();
    if (this.timer) window.clearInterval(this.timer);
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.timer = 0;
    this.frame = 0;
    if (!active || !this.visible) return;
    if (this.cadence === 'tick') {
      this.timer = window.setInterval(() => {
        this.now = Date.now();
      }, TICK_MS);
      return;
    }
    const onFrame = () => {
      this.now = Date.now();
      this.frame = window.requestAnimationFrame(onFrame);
    };
    this.frame = window.requestAnimationFrame(onFrame);
  }

  dispose(): void {
    this.unmount?.();
    this.unmount = undefined;
    this.run(false);
  }
}
